import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Search, Mail, Phone, MapPin, ArrowRight, FileText, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function clientLastFirst(rec) {
  const company = (rec?.company || '').trim();
  if (company) return company;
  const name = (rec?.name || '').trim();
  if (!name) return '';
  const parts = name.split(/\s+/);
  if (parts.length === 1) return parts[0];
  const last = parts[parts.length - 1];
  const first = parts.slice(0, -1).join(' ');
  return `${last}, ${first}`;
}

const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function ClientsDirectory() {
  const navigate = useNavigate();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('last_activity'); // last_activity | name | estimate_count | total_quoted | total_accepted
  const [filter, setFilter] = useState('all'); // all | saved | has_accepted

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/clients/directory`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setRecords)
      .catch(() => toast.error('Could not load clients'))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    let list = records;
    if (filter === 'saved') list = list.filter(r => r.saved);
    if (filter === 'has_accepted') list = list.filter(r => r.accepted_count > 0);
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(r =>
        (r.name || '').toLowerCase().includes(q)
        || (r.company || '').toLowerCase().includes(q)
        || (r.email || '').toLowerCase().includes(q)
        || (r.phone || '').toLowerCase().includes(q)
        || (r.address1 || '').toLowerCase().includes(q)
        || (r.address2 || '').toLowerCase().includes(q)
        || (r.estimate_types || []).some(t => t.toLowerCase().includes(q))
      );
    }
    const ord = [...list];
    ord.sort((a, b) => {
      if (sort === 'name') return clientLastFirst(a).toLowerCase().localeCompare(clientLastFirst(b).toLowerCase());
      if (sort === 'estimate_count') return (b.estimate_count || 0) - (a.estimate_count || 0);
      if (sort === 'total_quoted') return (b.total_quoted || 0) - (a.total_quoted || 0);
      if (sort === 'total_accepted') return (b.total_accepted || 0) - (a.total_accepted || 0);
      return (b.last_estimate_at || '').localeCompare(a.last_estimate_at || '');
    });
    return ord;
  }, [records, search, sort, filter]);

  const stats = useMemo(() => {
    const t = records.length;
    const saved = records.filter(r => r.saved).length;
    const accepted = records.reduce((s, r) => s + (r.accepted_count || 0), 0);
    const totalAccepted = records.reduce((s, r) => s + (r.total_accepted || 0), 0);
    return { t, saved, accepted, totalAccepted };
  }, [records]);

  return (
    <div className="animate-fade-in" data-testid="clients-directory">
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D1C]" style={{ fontFamily: "'Chivo', sans-serif" }}>
            Clients
          </h1>
          <p className="text-sm text-[#6B7A73] mt-1">
            Everyone you've ever estimated for — grouped, sortable, searchable. Click any row to see their estimates.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-3 text-xs">
          <Stat label="Clients" value={stats.t} />
          <Stat label="Accepted" value={stats.accepted} hint="jobs" />
          <Stat label="Revenue" value={fmtMoney(stats.totalAccepted)} />
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7A73]" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, company, email, phone, address, job type…"
            className="bg-white border border-[#E6EBE8] rounded-md pl-9 pr-3 py-2 text-sm w-full focus:outline-none focus:border-[#2D6B68]"
            data-testid="clients-search"
          />
        </div>
        <select
          value={filter}
          onChange={e => setFilter(e.target.value)}
          className="bg-white border border-[#E6EBE8] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#2D6B68]"
          data-testid="clients-filter"
        >
          <option value="all">All clients</option>
          <option value="saved">Saved only</option>
          <option value="has_accepted">Has accepted estimate</option>
        </select>
        <select
          value={sort}
          onChange={e => setSort(e.target.value)}
          className="bg-white border border-[#E6EBE8] rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#2D6B68]"
          data-testid="clients-sort"
        >
          <option value="last_activity">Last activity</option>
          <option value="name">Name (Last, First)</option>
          <option value="estimate_count">Most estimates</option>
          <option value="total_quoted">Total quoted</option>
          <option value="total_accepted">Total accepted</option>
        </select>
      </div>

      {loading ? (
        <div className="bg-white border border-[#E6EBE8] rounded-md p-8 text-center">
          <div className="w-6 h-6 border-2 border-[#2D6B68] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-[#E6EBE8] rounded-md p-8 text-center text-sm text-[#6B7A73]">
          {records.length === 0
            ? 'No clients yet. Create an estimate and the client will appear here.'
            : 'No clients match your search.'}
        </div>
      ) : (
        <div className="bg-white border border-[#E6EBE8] rounded-md overflow-hidden" data-testid="clients-table">
          <table className="w-full min-w-[900px]">
            <thead>
              <tr className="bg-[#F9F9F8] border-b border-[#E6EBE8]">
                <th className="text-left text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-3 font-semibold">Client</th>
                <th className="text-left text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-3 font-semibold">Contact</th>
                <th className="text-left text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-3 font-semibold hidden lg:table-cell">Job types</th>
                <th className="text-center text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-3 font-semibold">Estimates</th>
                <th className="text-right text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-3 font-semibold hidden md:table-cell">Accepted $</th>
                <th className="text-left text-xs uppercase tracking-wider text-[#6B7A73] py-3 px-3 font-semibold hidden lg:table-cell">Last activity</th>
                <th className="py-3 px-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E6EBE8]">
              {filtered.map(rec => (
                <ClientRow key={rec.client_id || (rec.email || '') + rec.name} rec={rec} navigate={navigate} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint }) {
  return (
    <div className="bg-white border border-[#E6EBE8] rounded-md px-3 py-2 text-center min-w-[80px]">
      <p className="text-[10px] uppercase tracking-[0.15em] text-[#6B7A73] font-semibold">{label}</p>
      <p className="text-lg font-semibold text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
        {value} {hint && <span className="text-[10px] text-[#6B7A73] font-normal">{hint}</span>}
      </p>
    </div>
  );
}

function ClientRow({ rec, navigate }) {
  const display = clientLastFirst(rec);
  const email = (rec.email || '').trim().toLowerCase();
  // Build the key the backend uses so we can navigate to /clients/<key>
  const key = email ? `e:${email}` : `n:${(rec.name || '').toLowerCase()}|${(rec.company || '').toLowerCase()}`;
  const encodedKey = encodeURIComponent(key);
  const address = [rec.address1, rec.address2, rec.postal_code].filter(Boolean).join(', ');
  return (
    <tr
      className="hover:bg-[#F9F9F8] transition-colors cursor-pointer"
      onClick={() => navigate(`/clients/${encodedKey}`)}
      data-testid={`client-row-${encodedKey}`}
    >
      <td className="py-3 px-3 max-w-[240px]">
        <div className="flex items-center gap-2">
          <p className="text-sm font-medium text-[#1A1D1C] truncate" title={display}>{display || '—'}</p>
          {rec.saved && <CheckCircle2 className="w-3.5 h-3.5 text-[#2D6B68]" title="Saved client" />}
        </div>
        {rec.company && rec.name && (
          <p className="text-[11px] text-[#6B7A73] truncate">{rec.name}</p>
        )}
        {address && (
          <p className="text-[11px] text-[#6B7A73] truncate mt-0.5 flex items-center gap-1">
            <MapPin className="w-2.5 h-2.5 flex-shrink-0" /> {address}
          </p>
        )}
      </td>
      <td className="py-3 px-3 max-w-[220px]">
        {rec.email && (
          <p className="text-xs text-[#3A423E] truncate flex items-center gap-1.5" title={rec.email}>
            <Mail className="w-3 h-3 text-[#6B7A73] flex-shrink-0" /> {rec.email}
          </p>
        )}
        {rec.phone && (
          <p className="text-xs text-[#3A423E] truncate flex items-center gap-1.5 mt-0.5" title={rec.phone}>
            <Phone className="w-3 h-3 text-[#6B7A73] flex-shrink-0" /> {rec.phone}
          </p>
        )}
        {!rec.email && !rec.phone && <span className="text-[11px] text-[#9BA5A0]">—</span>}
      </td>
      <td className="py-3 px-3 hidden lg:table-cell max-w-[200px]">
        {(rec.estimate_types || []).length === 0 ? (
          <span className="text-[11px] text-[#9BA5A0]">—</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {rec.estimate_types.slice(0, 3).map(t => (
              <span key={t} className="text-[10px] font-medium text-[#2D6B68] bg-[#E5F0EE] px-1.5 py-0.5 rounded whitespace-nowrap">
                {t}
              </span>
            ))}
            {rec.estimate_types.length > 3 && (
              <span className="text-[10px] text-[#6B7A73]">+{rec.estimate_types.length - 3}</span>
            )}
          </div>
        )}
      </td>
      <td className="py-3 px-3 text-center">
        <div className="inline-flex items-center gap-1 text-sm text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
          <FileText className="w-3 h-3 text-[#6B7A73]" /> {rec.estimate_count || 0}
        </div>
        {rec.accepted_count > 0 && (
          <p className="text-[10px] text-[#2D6B68] mt-0.5">{rec.accepted_count} accepted</p>
        )}
      </td>
      <td className="py-3 px-3 text-right hidden md:table-cell">
        {rec.total_accepted > 0 ? (
          <span className="text-sm font-medium text-[#1A1D1C]" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {fmtMoney(rec.total_accepted)}
          </span>
        ) : (
          <span className="text-[11px] text-[#9BA5A0]">—</span>
        )}
        {rec.total_quoted > rec.total_accepted && (
          <p className="text-[10px] text-[#6B7A73] mt-0.5">quoted {fmtMoney(rec.total_quoted)}</p>
        )}
      </td>
      <td className="py-3 px-3 hidden lg:table-cell">
        <p className="text-xs text-[#6B7A73]">
          {rec.last_estimate_at ? new Date(rec.last_estimate_at).toLocaleDateString() : '—'}
        </p>
        {rec.last_estimate_number && (
          <p className="text-[10px] text-[#9BA5A0] mt-0.5" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {rec.last_estimate_number}
          </p>
        )}
      </td>
      <td className="py-3 px-3 text-right">
        <ArrowRight className="w-4 h-4 text-[#C8D4CE] group-hover:text-[#2D6B68]" />
      </td>
    </tr>
  );
}
